import React, { useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Price } from '@thd-olt-component-react/price';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import {
  number, string, bool, shape, func
} from 'prop-types';
import { Promotion } from '@thd-olt-component-react/promotion';
import { Button } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod as ProductPodOlt,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductIdentifier,
  ProductImage, PodFooter, PodSection, ProductCompare, ProductSponsored, PodSpacer, PodSticker
} from '@thd-olt-component-react/product-pod';
import './ProductPod.scss';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { Analytics } from '../../analytics/sendAnalyticsEvent';
import { getHasConditionalPromotions } from '../../utils/cabinet-utils';

export const ProductPod = ({
  itemId, storeId, index, isMobile, product, handleQuickView
}) => {
  const { channel } = useContext(ExperienceContext);
  const hasConditionalPromotions = getHasConditionalPromotions({ product });
  let className = 'sui-flex product-pod-inner';

  if (isMobile) {
    className += ' mobile';
  }

  const openQuickViewDrawer = (event, sku, quickViewATC) => {
    event.preventDefault();
    event.stopPropagation();
    handleQuickView({ itemId: sku, isOpen: true, quickViewATC });
    Analytics.sendAnalyticsEvent({
      category: {
        primaryCategory: 'overlay'
      },
      eventInfo: {
        eventName: 'pip quick view'
      }
    });
  };

  return (
    <ProductPodOlt
      itemId={itemId}
      analyticsData={{
        parent: 'plp', position: index
      }}
      render={(pod) => (
        <div className={className}>
          <div>
            <ProductImage
              itemId={pod.itemId}
              showSecondaryImage={pod.showSecondaryImage}
            />
          </div>
          <PodSticker position="top-right">
            <PodSpacer padding={['TOP', 'RIGHT']}>
              <AddToList
                itemId={pod.itemId}
                showCount
                showIconButton
              />
            </PodSpacer>
          </PodSticker>
          <div className="product-pod-inner-body">
            <PodSection>
              <ProductSample itemId={pod.itemId} />
              <PodSpacer minHeight="60px" padding={['BOTTOM']}>
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  onSavingsCenterToggle={pod.onSavingsCenterToggle}
                  showPreferredPricingBadge
                  showPreferredPricingBadgeToolTip
                  hidePromotions={hasConditionalPromotions}
                />
              </PodSpacer>
              <Promotion
                channel={channel}
                itemId={pod.itemId}
                pageType="PLP"
                hideConditionalPromoDescription
              />
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <ProductRating itemId={pod.itemId} />
              </PodSpacer>
              <ProductHeader
                itemId={pod.itemId}
                brand="inline"
                disableShopThisCollection
                large={false}
                titleMaxLine={2}
                brandMaxLine={1}
              />
            </PodSection>
            <PodFooter>
              <PodSpacer minHeight={isMobile ? '100px' : '145px'} padding={['TOP']}>
                <FulfillmentPodStore directData={product} storeId={pod.storeId} />
                <FulfillmentPodShipping directData={product} storeId={pod.storeId} />
              </PodSpacer>
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <ProductQuantity
                  itemId={pod.itemId}
                  onQuantityChange={pod.onQuantityUpdate}
                  mediumWidth
                  wideView
                  noRightMargin
                />
              </PodSpacer>
              <PodSpacer minHeight="40px" padding={['TOP', 'BOTTOM']}>
                <ProductATC
                  itemId={pod.itemId}
                  storeId={storeId}
                  checkGeneric
                  outline
                  quantity={pod.quantity}
                  variant="secondary"
                />
              </PodSpacer>
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <div className="quick-view">
                  <Button
                    variant="ghost"
                    onClick={(event) => openQuickViewDrawer(event, pod.itemId, (
                      <AddToCart
                        itemId={pod.itemId}
                        cartReqParams={{ quantity: 1, itemId: pod.itemId }}
                      >
                        Add Cart
                      </AddToCart>
                    ))}
                  >
                    Quick View
                  </Button>
                </div>
              </PodSpacer>
            </PodFooter>
          </div>
        </div>
      )}
    />
  );
};

ProductPod.propTypes = {
  itemId: string,
  storeId: string,
  index: number,
  isMobile: bool,
  product: shape({
    itemId: string,
    quantity: number
  }),
  handleQuickView: func.isRequired
};

ProductPod.defaultProps = {
  itemId: '',
  storeId: '',
  index: undefined,
  isMobile: false,
  product: {}
};

ProductPod.dataModel = extend(
  ProductPodOlt,
  AddToList,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  KeyProductFeatures,
  Price,
  ProductATC,
  ProductCompare,
  ProductHeader,
  ProductIdentifier,
  ProductImage,
  ProductQuantity,
  ProductRating,
  ProductSample,
  ProductSponsored,
  Promotion
);

ProductPod.displayName = 'ProductPod';
